import { Injectable } from '@angular/core';

import { DateRange } from '@angular/material/datepicker';

import {
  ColDef,
  ICellRendererParams,
  IServerSideGetRowsParams,
  RowHeightParams,
  ValueFormatterParams,
  ValueGetterParams
} from 'ag-grid-enterprise';

import moment from 'moment';

import { LinkRendererComponent } from '@components/table/cell-renderers/link-renderer/link-renderer.component';
import { IconRendererComponent } from '@components/table/cell-renderers/icon-renderer/icon-renderer.component';
import { ListService } from '@services/list.service';
import { TimeService } from '@services/time.service';
import { TableService } from '@services/table.service';
import { UtilitiesService } from '@services/utilities.service';
import { ORDER_STATUSES } from '@configs/order';
import { ORDER_PAYMENT_STATUS_CONTROL_NAME_VALUE_MAPPER, ORDER_PAYMENT_STATUSES } from '@configs/checkbox';
import { PAGINATION_PAGE_SIZE } from '@configs/select';
import { TableConfig } from '@models/table';
import { FilterSectionConfig, SearchBoxConfig, SearchBoxValue } from '@models/search-box';
import { QueryParams } from '@models/api';
import { User } from '@models/user';
import { OrdersByStatus } from '@models/report';
import { Checkbox } from '@models/checkbox';

@Injectable({
  providedIn: 'root'
})
export class ReportService {
  constructor(
    private listService: ListService,
    private timeService: TimeService,
    private tableService: TableService,
    private utilitiesService: UtilitiesService
  ) { }

  public getJobsByCompanyListTableConfig(): TableConfig {
    return {
      colDefs: [
        {
          headerName: '',
          field: 'company_from_qualia',
          cellRenderer: IconRendererComponent,
          valueGetter: (params: ValueGetterParams) => this.tableService.getIconRendererParams(params.data),
          sortable: false,
          minWidth: 68,
          maxWidth: 68
        },
        {
          headerName: 'Company',
          field: 'company_name',
          cellRenderer: LinkRendererComponent,
          cellRendererParams: {
            customParams: {
              static: '/company-edit',
              dynamic: 'company_id',
              external: true
            }
          },
          minWidth: 300
        },
        {
          headerName: 'Total Ordered',
          field: 'ordered',
          valueFormatter: this.utilitiesService.numberFormatter
        },
        {
          headerName: 'Cancelled',
          field: 'cancelled',
          valueFormatter: this.utilitiesService.numberFormatter
        },
        {
          headerName: 'Total Billed',
          field: 'total_billed',
          valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params)
        },
        {
          headerName: 'Total Unpaid',
          field: 'unpaid_status',
          valueFormatter: this.utilitiesService.numberFormatter
        },
        {
          headerName: 'Total Partially Paid',
          field: 'partially_paid_status',
          valueFormatter: this.utilitiesService.numberFormatter
        },
        {
          headerName: 'Total Full Paid',
          field: 'total_paid_status',
          valueFormatter: this.utilitiesService.numberFormatter
        },
        {
          headerName: 'First Order Date',
          field: 'first_order_date',
          valueFormatter: (params: ValueFormatterParams) => this.timeService.timestampFormatter(params)
        },
        {
          headerName: 'Last Order Date',
          field: 'last_order_date',
          valueFormatter: (params: ValueFormatterParams) => this.timeService.timestampFormatter(params)
        },
        {
          headerName: 'Marketer',
          field: 'client_rep',
          valueFormatter: this.utilitiesService.textFormatter
        }
      ],
      paginationPageSizeItems: PAGINATION_PAGE_SIZE
    };
  }

  public getJobsByCountyListTableConfig(): TableConfig {
    return {
      colDefs: [
        {
          headerName: 'State',
          field: 'state',
          minWidth: 100,
          maxWidth: 100,
          valueFormatter: this.utilitiesService.textFormatter
        },
        {
          headerName: 'County',
          field: 'county',
          valueFormatter: this.utilitiesService.textFormatter
        },
        {
          headerName: 'City',
          field: 'city',
          valueFormatter: this.utilitiesService.textFormatter
        },
        {
          headerName: 'Quantity',
          field: 'qty',
          maxWidth: 150
        }
      ],
      paginationPageSizeItems: PAGINATION_PAGE_SIZE
    };
  }

  public getSalesByMarketerListTableConfig(): TableConfig {
    const monthCols: Array<ColDef> = [];
    let monthNumber: number = this.timeService.getCurrentMonthNumber();

    for (let i = 0; i <= 12; i++) {
      monthCols.push({
        headerName: this.timeService.getMonthNameByNumber(monthNumber),
        field: `${i + 1}_month_qty`,
        columnGroupShow: 'open',
        maxWidth: 150
      });

      monthNumber--;
    }

    return {
      colDefs: [
        {
          headerName: '',
          field: 'company_from_qualia',
          cellRenderer: IconRendererComponent,
          valueGetter: (params: ValueGetterParams) => this.tableService.getIconRendererParams(params.data),
          sortable: false,
          minWidth: 68,
          maxWidth: 68
        },
        {
          headerName: 'Marketer',
          field: 'marketer'
        },
        {
          headerName: 'Client',
          field: 'company_name'
        },
        {
          headerName: 'First Order Date',
          field: 'first_order_date',
          valueFormatter: (params: ValueFormatterParams) => this.timeService.timestampFormatter(params)
        },
        {
          headerName: 'Details',
          children: [
            {
              headerName: 'Year Quantity',
              field: 'year_qty',
              maxWidth: 150
            },
            ...monthCols
          ]
        }
      ],
      paginationPageSizeItems: PAGINATION_PAGE_SIZE
    };
  }

  public getSkippedTaskListTableConfig(): TableConfig {
    return {
      colDefs: [
        {
          headerName: 'ID',
          field: 'epr_id',
          valueFormatter: this.utilitiesService.textFormatter,
          minWidth: 110,
          maxWidth: 110
        },
        {
          headerName: 'Ordered',
          field: 'created',
          valueFormatter: (params: ValueFormatterParams) => this.timeService.timestampFormatter(params),
          minWidth: 100,
          maxWidth: 100
        },
        {
          headerName: 'Client',
          field: 'first_name',
          valueFormatter: (params: ValueFormatterParams) => `${params.value} ${params.data.last_name}`
        },
        {
          headerName: 'Address',
          field: 'order_address_1',
          valueFormatter: this.utilitiesService.addressFormatter
        },
        {
          headerName: 'Status',
          field: 'status'
        },
        {
          headerName: 'Pmt Status',
          field: 'payment_status',
          minWidth: 100,
          maxWidth: 100
        },
        {
          headerName: 'Billed',
          field: 'billed_date',
          valueFormatter: (params: ValueFormatterParams) => this.timeService.timestampFormatter(params),
          minWidth: 100,
          maxWidth: 100
        },
        {
          headerName: 'Skipped Tasks',
          field: 'skipped_tasks',
          minWidth: 400,
          cellRenderer: (params: ICellRendererParams) =>this.utilitiesService.noteCellRenderer(params, ',')
        },
        {
          headerName: 'Skipped Tasks Notes',
          field: 'skipped_notes',
          cellRenderer: (params: ICellRendererParams) =>this.utilitiesService.noteCellRenderer(params, ','),
          autoHeight: true,
          minWidth: 500
        }
      ],
      paginationPageSizeItems: PAGINATION_PAGE_SIZE
    };
  }

  public getOrderListTableConfig(): TableConfig {
    return {
      colDefs: [
        {
          headerName: 'ID',
          field: 'epr_id',
          valueFormatter: this.utilitiesService.textFormatter,
          minWidth: 110,
          maxWidth: 110
        },
        {
          headerName: 'Status',
          field: 'status',
          minWidth: 160,
          maxWidth: 160
        },
        {
          headerName: 'Assigned',
          field: 'assigned_to_display_name',
          valueFormatter: this.utilitiesService.textFormatter,
          minWidth: 190,
          maxWidth: 190
        },
        {
          headerName: 'Ordered',
          field: 'created',
          valueFormatter: (params: ValueFormatterParams) => this.timeService.timestampFormatter(params),
          minWidth: 100,
          maxWidth: 100
        },
        {
          headerName: 'Client',
          field: 'first_name',
          valueFormatter: (params: ValueFormatterParams) => `${params.value} ${params.data.last_name || ''}`
        },
        {
          headerName: 'Company',
          field: 'company_name',
          wrapText: true,
          minWidth: 200
        },
        {
          headerName: 'Address Info',
          children: [
            {
              headerName: 'Address',
              field: 'full_address',
              minWidth: 300
            },
            {
              headerName: 'County',
              field: 'order_county',
              columnGroupShow: 'open'
            },
            {
              headerName: 'State',
              field: 'order_state',
              maxWidth: 100,
              columnGroupShow: 'open'
            }
          ]
        },
        {
          headerName: 'File',
          field: 'order_file_number',
          wrapText: true,
          valueFormatter: this.utilitiesService.textFormatter
        },
        {
          headerName: 'Dates',
          children: [
            {
              headerName: 'Promised',
              field: 'order_promise_date_at',
              valueFormatter: (params: ValueFormatterParams) => this.timeService.timestampFormatter(params)
            },
            {
              headerName: 'Needed',
              field: 'order_needed_date_at',
              valueFormatter: (params: ValueFormatterParams) => this.timeService.timestampFormatter(params),
              columnGroupShow: 'open'
            },
            {
              headerName: 'Closing',
              field: 'order_closing_date_at',
              valueFormatter: (params: ValueFormatterParams) => this.timeService.timestampFormatter(params),
              columnGroupShow: 'open'
            }
          ]
        },
        {
          headerName: 'Rush',
          field: 'order_check_rush_order',
          valueFormatter: this.utilitiesService.yesNoFormatter,
          maxWidth: 100
        }
      ]
    };
  }

  public getCompanyStatementListTableConfig(): TableConfig {
    return {
      colDefs: [
        {
          headerName: '',
          field: 'company_from_qualia',
          cellRenderer: IconRendererComponent,
          valueGetter: (params: ValueGetterParams) => this.tableService.getIconRendererParams(params.data),
          sortable: false,
          minWidth: 68,
          maxWidth: 68
        },
        {
          headerName: 'Ordered By',
          field: 'company_name',
          cellRenderer: LinkRendererComponent,
          cellRendererParams: {
            customParams: {
              static: '/company-edit',
              dynamic: 'company_id',
              external: true
            }
          },
          autoHeight: true
        },
        {
          headerName: 'First Order Date',
          field: 'first_order_date',
          valueFormatter: (params: ValueFormatterParams) => this.timeService.timestampFormatter(params)
        },
        {
          headerName: 'Last Order Date',
          field: 'last_order_date',
          valueFormatter: (params: ValueFormatterParams) => this.timeService.timestampFormatter(params)
        },
        {
          headerName: 'Days',
          children: [
            {
              headerName: '0-30',
              field: 'days30',
              valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params)
            },
            {
              headerName: '31-60',
              field: 'days60',
              valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params),
              columnGroupShow: 'open'
            },
            {
              headerName: '61-90',
              field: 'days90',
              valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params),
              columnGroupShow: 'open'
            },
            {
              headerName: '91-120',
              field: 'days120',
              valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params),
              columnGroupShow: 'open'
            },
            {
              headerName: '121-180',
              field: 'days180',
              valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params),
              columnGroupShow: 'open'
            },
            {
              headerName: '181+',
              field: 'days181',
              valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params),
              columnGroupShow: 'open'
            }
          ]
        },
        {
          headerName: 'Balance',
          field: 'balance',
          valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params)
        },
        {
          headerName: 'Notes',
          field: 'special_instructions',
          cellRenderer: this.utilitiesService.noteCellRenderer,
          autoHeight: true,
          wrapText: true
        }
      ],
      autoCellWidth: true,
      paginationPageSizeItems: PAGINATION_PAGE_SIZE
    };
  }

  public getUserStatementListTableConfig(): TableConfig {
    return {
      colDefs: [
        {
          headerName: '',
          field: 'company_from_qualia',
          cellRenderer: IconRendererComponent,
          valueGetter: (params: ValueGetterParams) => this.tableService.getIconRendererParams(params.data),
          sortable: false,
          minWidth: 68,
          maxWidth: 68
        },
        {
          headerName: 'Ordered By',
          field: 'ordered_by',
          cellRenderer: LinkRendererComponent,
          cellRendererParams: {
            customParams: {
              static: '/user-edit',
              dynamic: 'user_id',
              external: true
            }
          }
        },
        {
          headerName: 'ID',
          field: 'epr_id',
          valueFormatter: this.utilitiesService.textFormatter,
          minWidth: 110,
          maxWidth: 110
        },
        {
          headerName: 'Product',
          field: 'product'
        },
        {
          headerName: 'Company',
          field: 'company_name',
          cellRenderer: LinkRendererComponent,
          cellRendererParams: {
            customParams: {
              static: '/company-edit',
              dynamic: 'company_id',
              external: true
            }
          }
        },
        {
          headerName: 'Status',
          field: 'order_status'
        },
        {
          headerName: 'File',
          field: 'client_file_number',
          valueFormatter: this.utilitiesService.textFormatter
        },
        {
          headerName: 'Address',
          field: 'address_1',
          valueFormatter: this.utilitiesService.addressFormatter
        },
        {
          headerName: 'Ordered',
          field: 'ordered_date',
          valueFormatter: (params: ValueFormatterParams) => this.timeService.timestampFormatter(params)
        },
        {
          headerName: 'Billed',
          field: 'billed_date',
          valueFormatter: (params: ValueFormatterParams) => this.timeService.timestampFormatter(params)
        },
        {
          headerName: 'Payment',
          children: [
            {
              headerName: 'Status',
              field: 'payment_status'
            },
            {
              headerName: 'Order Total',
              field: 'order_total',
              valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params),
              columnGroupShow: 'open'
            },
            {
              headerName: 'Total Payments',
              field: 'total_payment',
              valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params),
              columnGroupShow: 'open'
            },
            {
              headerName: 'Payments',
              field: 'payments_count',
              columnGroupShow: 'open'
            },
            {
              headerName: 'Last Payment',
              field: 'last_payment_date',
              valueFormatter: (params: ValueFormatterParams) => this.timeService.timestampFormatter(params),
              columnGroupShow: 'open'
            },
            {
              headerName: 'Balance',
              field: 'balance',
              valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params),
              columnGroupShow: 'open'
            },
            {
              headerName: '0-30',
              field: 'days30',
              valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params),
              columnGroupShow: 'open'
            },
            {
              headerName: '31-60',
              field: 'days60',
              valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params),
              columnGroupShow: 'open'
            },
            {
              headerName: '61-90',
              field: 'days90',
              valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params),
              columnGroupShow: 'open'
            },
            {
              headerName: '91-120',
              field: 'days120',
              valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params),
              columnGroupShow: 'open'
            },
            {
              headerName: '121-180',
              field: 'days180',
              valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params),
              columnGroupShow: 'open'
            },
            {
              headerName: '181+',
              field: 'days181',
              valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params),
              columnGroupShow: 'open'
            }
          ]
        }
      ],
      paginationPageSizeItems: PAGINATION_PAGE_SIZE
    };
  }

  public getOrdersByResearcherListTableConfig(): TableConfig {
    return {
      colDefs: [
        {
          headerName: 'Researcher',
          field: 'full_name',
          pinned: 'left'
        },
        {
          headerName: 'Total',
          field: 'cnt',
          sortable: false,
          minWidth: 60
        },
        {
          headerName: ORDER_STATUSES.NEW,
          field: 'orders_by_statuses',
          valueFormatter: (params: ValueFormatterParams) => {
            return this.getOrderQuantityByStatus(params.value, ORDER_STATUSES.NEW);
          },
          sortable: false,
          minWidth: 60
        },
        {
          headerName: ORDER_STATUSES.ASSIGNED,
          field: 'orders_by_statuses',
          valueFormatter: (params: ValueFormatterParams) => {
            return this.getOrderQuantityByStatus(params.value, ORDER_STATUSES.ASSIGNED)
          },
          sortable: false,
          minWidth: 60
        },
        {
          headerName: ORDER_STATUSES.RESEARCH,
          field: 'orders_by_statuses',
          valueFormatter: (params: ValueFormatterParams) => {
            return this.getOrderQuantityByStatus(params.value, ORDER_STATUSES.RESEARCH);
          },
          sortable: false,
          minWidth: 60
        },
        {
          headerName: ORDER_STATUSES.TO_BE_BUILT,
          field: 'orders_by_statuses',
          valueFormatter: (params: ValueFormatterParams) => {
            return this.getOrderQuantityByStatus(params.value, ORDER_STATUSES.TO_BE_BUILT);
          },
          sortable: false,
          minWidth: 60
        },
        {
          headerName: ORDER_STATUSES.REVIEW,
          field: 'orders_by_statuses',
          valueFormatter: (params: ValueFormatterParams) => {
            return this.getOrderQuantityByStatus(params.value, ORDER_STATUSES.REVIEW);
          },
          sortable: false,
          minWidth: 60
        },
        {
          headerName: ORDER_STATUSES.HOLD,
          field: 'orders_by_statuses',
          valueFormatter: (params: ValueFormatterParams) => {
            return this.getOrderQuantityByStatus(params.value, ORDER_STATUSES.HOLD);
          },
          sortable: false,
          minWidth: 60
        }
      ]
    };
  }

  public getCompletedOrdersByResearcherListTableConfig(): TableConfig {
    return {
      colDefs: [
        {
          headerName: 'Researcher',
          field: 'name'
        },
        {
          headerName: 'Total Send To Client',
          field: 'total_sent_to_client_researcher'
        },
        {
          headerName: 'Researcher Orders',
          field: 'total_sent_to_client'
        },
        {
          headerName: 'Researcher %',
          field: 'total_sent_to_client',
          valueFormatter: (params: ValueFormatterParams) => {
            return this.utilitiesService.percentFormatter(
              params.value / params.data.total_sent_to_client_researcher,
              '1.2'
            );
          }
        }
      ],
      paginationPageSizeItems: PAGINATION_PAGE_SIZE
    };
  }

  public getOrderIssuesByResearchersListTableConfig(): TableConfig {
    return {
      colDefs: [
        {
          headerName: 'Researcher',
          field: 'researcher_display_name'
        },
        {
          headerName: 'Issues',
          field: 'order_error_count'
        }
      ],
      paginationPageSizeItems: PAGINATION_PAGE_SIZE
    };
  }

  public getOrderIssuesByResearcherListTableConfig(): TableConfig {
    return {
      colDefs: [
        {
          headerName: 'Order',
          field: 'epr_id',
          cellRenderer: LinkRendererComponent,
          cellRendererParams: {
            customParams: {
              static: '/order-edit',
              dynamic: 'id',
              external: true
            }
          }
        },
        {
          headerName: 'Quality Control',
          field: 'qc_display_name',
          cellRenderer: (params: ICellRendererParams) => this.utilitiesService.noteCellRenderer(params, ','),
          valueFormatter: (params: ValueFormatterParams) => `${params.value}`,
          minWidth: 400
        },
        {
          headerName: 'Statuses',
          children: [
            {
              headerName: 'Assigned',
              field: 'assigned'
            },
            {
              headerName: 'Completed',
              field: 'completed',
              columnGroupShow: 'open'
            },
            {
              headerName: 'Pending Review',
              field: 'pending_review',
              columnGroupShow: 'open'
            },
            {
              headerName: 'Cancelled',
              field: 'cancelled',
              columnGroupShow: 'open'
            },
            {
              headerName: 'Pending Cancel',
              field: 'pending_cancel',
              columnGroupShow: 'open'
            },
            {
              headerName: 'Deleted',
              field: 'deleted',
              columnGroupShow: 'open'
            }
          ]
        },
        {
          headerName: 'First Issue Date',
          field: 'first_issue_date',
          valueFormatter: (params: ValueFormatterParams) => this.timeService.timestampFormatter(params),
        }
      ],
      getRowHeight: (params: RowHeightParams): number | undefined | null => {
        if (!params.data) { return null; }
        const qas: string = params.data.qc_display_name,
              lineCounter: number = qas ? qas.split(',').length : 1;
        return lineCounter > 2 ? 20 * lineCounter : 48;
      }
    };
  }

  public getOrdersToResearchStatusListTableConfig(): TableConfig {
    return {
      colDefs:[
        {
          headerName: 'Researcher',
          field: 'name'
        },
        {
          headerName: 'Total Orders To Research Status',
          field: 'total_orders'
        },
        {
          headerName: 'Researcher Orders',
          field: 'researcher_total'
        },
        {
          headerName: 'Researcher %',
          field: 'researcher_total',
          valueFormatter: (params: ValueFormatterParams) => {
            return this.utilitiesService.percentFormatter(params.value / params.data.total_orders, '1.2');
          }
        }
      ],
      paginationPageSizeItems: PAGINATION_PAGE_SIZE
    };
  }

  public getRevenueByResearcherListTableConfig(): TableConfig {
    return {
      colDefs: [
        {
          headerName: 'Researcher',
          field: 'name'
        },
        {
          headerName: 'Total Orders',
          field: 'orders_count'
        },
        {
          headerName: 'Researcher Orders',
          field: 'researcher_orders_count'
        },
        {
          headerName: 'Total',
          children: [
            {
              headerName: 'Order Cost',
              field: 'researcher_orders_cost_total',
              valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params)
            },
            {
              headerName: 'Search Fee',
              field: 'search_fee_total',
              valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params),
              columnGroupShow: 'open'
            },
            {
              headerName: 'Hard Costs',
              field: 'hard_costs_total',
              valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params),
              columnGroupShow: 'open'
            },
            {
              headerName: 'S & H',
              field: 'shipping_handling_total',
              valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params),
              columnGroupShow: 'open'
            }
          ]
        },
        {
          headerName: 'Misc. Amount',
          field: 'misc_amount',
          valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params)
        },
        {
          headerName: 'Discount Amount',
          field: 'discount_amount',
          valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params)
        },
        {
          headerName: 'Revenue',
          field: 'revenue_percentage',
          valueFormatter: (params: ValueFormatterParams) => `${params.value} %`
        },
        {
          headerName: 'Order %',
          field: 'order_percentage',
          valueFormatter: (params: ValueFormatterParams) => `${params.value} %`
        }
      ],
      paginationPageSizeItems: PAGINATION_PAGE_SIZE
    };
  }

  public getCommissionsPaidByMarketerListTableConfig(): TableConfig {
    return {
      colDefs: [
        {
          headerName: 'Marketer',
          field: 'marketer',
          cellRenderer: LinkRendererComponent,
          cellRendererParams: {
            customParams: {
              static: '/user-edit',
              dynamic: 'marketer_user_id',
              external: true
            }
          }
        },
        {
          headerName: 'Company',
          field: 'name',
          wrapText: true,
          minWidth: 200
        },
        {
          headerName: 'Order',
          field: 'epr_id',
          valueFormatter: this.utilitiesService.textFormatter,
        },
        {
          headerName: 'Status',
          field: 'status'
        },
        {
          headerName: 'Payment Status',
          field: 'payment_status'
        },
        {
          headerName: 'First Order Date',
          field: 'first_order_date',
          valueFormatter: (params: ValueFormatterParams) => this.timeService.timestampFormatter(params)
        },
        {
          headerName: 'Total',
          field: 'order_total',
          valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params)
        },
        {
          headerName: 'Search Fee',
          field: 'search_fee',
          valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params)
        },
        {
          headerName: 'Hard Costs',
          field: 'hard_costs',
          valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params)
        },
        {
          headerName: 'Misc. Amount',
          field: 'misc_amount',
          valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params)
        },
        {
          headerName: 'Paid',
          field: 'paid',
          valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params)
        },
        {
          headerName: 'Address',
          children: [
            {
              headerName: 'Address',
              field: 'address'
            },
            {
              headerName: 'State',
              field: 'state',
              columnGroupShow: 'open'
            },
            {
              headerName: 'County',
              field: 'county',
              columnGroupShow: 'open'
            },
            {
              headerName: 'City',
              field: 'city',
              columnGroupShow: 'open'
            },
            {
              headerName: 'Zip',
              field: 'zip',
              columnGroupShow: 'open'
            }
          ]
        }
      ],
      paginationPageSizeItems: PAGINATION_PAGE_SIZE
    };
  }

  public getCommissionsOrderedByMarketerListTableConfig(): TableConfig {
    return {
      colDefs: [
        {
          headerName: 'Company',
          field: 'name',
          wrapText: true,
          minWidth: 200
        },
        {
          headerName: 'Send to client',
          field: 'sent_to_client_date',
          valueFormatter: (params: ValueFormatterParams) => this.timeService.timestampFormatter(params)
        },
        {
          headerName: 'Order',
          field: 'epr_id',
          valueFormatter: this.utilitiesService.textFormatter
        },
        {
          headerName: 'Product',
          field: 'product'
        },
        {
          headerName: 'Total',
          field: 'order_total',
          valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params)
        },
        {
          headerName: 'Search Fee',
          field: 'search_fee',
          valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params)
        },
        {
          headerName: 'Hard Costs',
          field: 'hard_costs',
          valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params)
        },
        {
          headerName: 'Misc. Amount',
          field: 'misc_amount',
          valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params)
        },
        {
          headerName: 'Paid',
          field: 'paid',
          valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.currencyFormatter(params)
        },
        {
          headerName: 'Address',
          children: [
            {
              headerName: 'Address',
              field: 'address'
            },
            {
              headerName: 'State',
              field: 'state',
              columnGroupShow: 'open'
            },
            {
              headerName: 'County',
              field: 'county',
              valueFormatter: this.utilitiesService.textFormatter,
              columnGroupShow: 'open'
            },
            {
              headerName: 'City',
              field: 'city',
              columnGroupShow: 'open'
            },
            {
              headerName: 'Zip',
              field: 'zip',
              columnGroupShow: 'open'
            }
          ]
        },
        {
          headerName: 'Status',
          field: 'status'
        },
        {
          headerName: 'Marketer',
          field: 'marketer',
          cellRenderer: LinkRendererComponent,
          cellRendererParams: {
            customParams: {
              static: '/user-edit',
              dynamic: 'marketer_user_id',
              external: true
            }
          }
        }
      ],
      paginationPageSizeItems: PAGINATION_PAGE_SIZE
    };
  }

  public getSentToClientByUserListTableConfig(): TableConfig {
    return {
      colDefs: [
        {
          headerName: 'Completed By',
          field: 'completed_by_email',
          valueFormatter: this.utilitiesService.textFormatter
        },
        {
          headerName: 'Role',
          field: 'completed_by_role',
          valueFormatter: this.utilitiesService.textFormatter
        },
        {
          headerName: 'Count',
          field: 'order_count'
        }
      ],
      paginationPageSizeItems: PAGINATION_PAGE_SIZE
    };
  }

  public getOrdersByStatusTableConfig(): TableConfig {
    return {
      colDefs: [
        {
          headerName: 'Status',
          field: 'label'
        },
        {
          headerName: 'Quantity',
          field: 'value'
        }
      ]
    };
  }

  public getAccountingForManagerListTableConfig(): TableConfig {
    return {
      colDefs: [
        {
          headerName: 'order ID',
          field: 'id',
          valueFormatter: this.utilitiesService.textFormatter,
          minWidth: 110,
          maxWidth: 110
        },
        {
          headerName: 'Product',
          field: 'product_name'
        },
        {
          headerName: 'Company',
          field: 'company_name'
        },
        {
          headerName: 'Ordered By',
          field: 'ordered_by'
        },
        {
          headerName: 'Status',
          field: 'status'
        },
        {
          headerName: 'Client File #',
          field: 'order_file_number',
          valueFormatter: this.utilitiesService.textFormatter
        },
        {
          headerName: 'Fendor ID',
          field: 'vendor_invoice_id',
          valueFormatter: this.utilitiesService.textFormatter
        },

        {
          headerName: 'Address',
          field: 'full_address',
          valueFormatter: this.utilitiesService.addressFormatter
        }
      ]
    };
  }

  public getSearchBoxConfig(dateValue: DateRange<moment.Moment> | null = null): SearchBoxConfig {
    return {
      searchSection: {
        search: true
      },
      filterSection: {
        date: true,
        dateValue
      },
      searchSectionActions: true
    };
  }

  public getJobsByCompanyListSearchBoxConfig(): SearchBoxConfig {
    const searchBoxConfig: SearchBoxConfig = this.getSearchBoxConfig(),
          filterSectionConfig: FilterSectionConfig = searchBoxConfig.filterSection;

    filterSectionConfig.onlyQualiaCompanies = true;
    filterSectionConfig.doBusinessAs = true;

    return searchBoxConfig;
  }

  public getJobsByCountyListSearchBoxConfig(): SearchBoxConfig {
    return {
      searchSection: {
        search: true
      },
      filterSection: {
        state: true,
        county: true,
        city: true
      },
      searchSectionActions: true
    };
  }

  public getSalesByMarketerListSearchBoxConfig(): SearchBoxConfig {
    return {
      searchSection: {
        search: true
      },
      filterSection: {
        marketer: true,
        onlyQualiaCompanies: true
      },
      searchSectionActions: true
    };
  }

  public getSkippedTaskListSearchBoxConfig(): SearchBoxConfig {
    return {
      searchSection: {
        search: true
      },
      filterSection: {
        orderStatuses: true,
        orderStatusItems: Object.values(ORDER_STATUSES),
        date: true,
        dateValue: this.timeService.getTodayDateTimeRange()
      },
      searchSectionActions: true
    };
  }

  public getOrderListSearchBoxConfig(): SearchBoxConfig {
    return {
      searchSection: {
        search: true
      },
      filterSection: {
        orderStatuses: true,
        orderStatusItems: Object.values(ORDER_STATUSES),
        date: true
      },
      searchSectionActions: true
    };
  }

  public getStatementListSearchBoxConfig(): SearchBoxConfig {
    return {
      searchSection: {
        search: true
      },
      filterSection: {
        orderStatuses: true,
        orderStatusItems: Object.values(ORDER_STATUSES),
        orderStatusValue: [
          ORDER_STATUSES.SENT_TO_CLIENT,
          ORDER_STATUSES.CANCELLATION_PENDING,
          ORDER_STATUSES.CANCELLED_HARD_COST_PAYABLE,
          ORDER_STATUSES.CANCELLED_ESTOPPEL_REFUND_NEEDED,
          ORDER_STATUSES.UPDATED
        ],
        orderPaymentStatuses: true,
        orderPaymentStatusItems: ORDER_PAYMENT_STATUSES.map((item: Checkbox) => {
          return {
            ...item,
            value: item.controlName === 'paid' ? item.value : true
          };
        }),
        onlyQualiaCompanies: true,
        doBusinessAs: true
      },
      searchSectionActions: true
    };
  }

  public getCommissionsByMarketerListSearchBoxConfig(): SearchBoxConfig {
    return {
      searchSection: {
        search: true
      },
      filterSection: {
        marketer: true,
        date: true,
        dateValue: this.timeService.getFromMonthStartToTodayDateTimeRange()
      },
      searchSectionActions: true
    };
  }

  public getAccountingForManagerListSearchBoxConfig(): SearchBoxConfig {
    return {
      searchSection: {
        search: true
      },
      filterSection: {
        date: true,
        dateValue: this.timeService.getFromMonthStartToTodayDateTimeRange(),
        orderStatuses: true,
        orderStatusItems: Object.values(ORDER_STATUSES),
        orderStatusValue: [
          ORDER_STATUSES.SENT_TO_CLIENT,
          ORDER_STATUSES.CANCELLED,
          ORDER_STATUSES.CANCELLATION_PENDING,
          ORDER_STATUSES.CANCELLED_HARD_COST_PAYABLE,
          ORDER_STATUSES.CANCELLED_ESTOPPEL_REFUND_NEEDED,
          ORDER_STATUSES.UPDATED
        ]
      },
      searchSectionActions: true
    }
  }

  public prepareQueryParams(value: SearchBoxValue, params: IServerSideGetRowsParams): QueryParams {
    const {start, end} = value.date;
    return {
      ...this.listService.prepareBaseQueryParams(value, params),
      from: start ? this.timeService.formatDateTime(this.timeService.addStartDay(start)) : null,
      to: end ? this.timeService.formatDateTime(this.timeService.addEndDay(end)) : null
    };
  }

  public prepareJobsByCompanyListQueryParams(value: SearchBoxValue, params: IServerSideGetRowsParams): QueryParams {
    const queryParams: QueryParams = this.prepareQueryParams(value, params);

    return {
      ...queryParams,
      onlyQualiaCompanies: value.onlyQualiaCompanies || null,
      doBusinessAs: value.doBusinessAs
    };
  }

  public prepareJobsByCountyListQueryParams(value: SearchBoxValue, params: IServerSideGetRowsParams): QueryParams {
    return {
      ...this.listService.prepareBaseQueryParams(value, params),
      state: value.state,
      county: value.county,
      city: value.city
    };
  }

  public prepareSalesByMarketerListQueryParams(value: SearchBoxValue, params: IServerSideGetRowsParams): QueryParams {
    return {
      ...this.listService.prepareBaseQueryParams(value, params),
      marketer: value.marketer?.id,
      onlyQualiaCompanies: value.onlyQualiaCompanies || null
    };
  }

  public prepareSkippedTaskListQueryParams(value: SearchBoxValue, params: IServerSideGetRowsParams): QueryParams {
    const {date, orderStatuses} = value,
          {start, end} = date;
    return {
      ...this.listService.prepareBaseQueryParams(value, params),
      statuses: orderStatuses ? orderStatuses.join(',') : null,
      from: start ? this.timeService.formatDateTime(this.timeService.addStartDay(start)) : null,
      to: end ? this.timeService.formatDateTime(this.timeService.addEndDay(end)) : null
    };
  }

  public prepareOrdersByNoTaskListQueryParams(value: SearchBoxValue, params: IServerSideGetRowsParams): QueryParams {
    const {date, orderStatuses, orderAssignee} = value,
          {start, end} = date;
    return {
      ...this.listService.prepareBaseQueryParams(value, params),
      no_tasks: 1,
      statuses: orderStatuses ? orderStatuses.join(',') : null,
      assigned_to: orderAssignee ? orderAssignee.map((user: User) => user.id).join(',') : null,
      from: start ? this.timeService.formatDateTime(this.timeService.addStartDay(start)) : null,
      to: end ? this.timeService.formatDateTime(this.timeService.addEndDay(end)) : null
    };
  }

  public prepareStatementListQueryParams(value: SearchBoxValue, params: IServerSideGetRowsParams): QueryParams {
    const {orderStatuses, orderPaymentStatuses} = value;
    return {
      ...this.listService.prepareBaseQueryParams(value, params),
      statuses: orderStatuses ? orderStatuses.join(',') : null,
      paymentStatuses: orderPaymentStatuses
        ? Object.entries(value.orderPaymentStatuses)
          .filter(([key, value]) => value ? key : value)
          .map(([key, value]) => ORDER_PAYMENT_STATUS_CONTROL_NAME_VALUE_MAPPER[key])
          .join(',')
        : null,
      company_id: value.company?.id,
      onlyQualiaCompanies: value.onlyQualiaCompanies || null,
      doBusinessAs: value.doBusinessAs
    };
  }

  public prepareOrdersByDueOverdueListQueryParams(value: SearchBoxValue, params: IServerSideGetRowsParams): QueryParams {
    const {date, orderStatuses, orderAssignee} = value,
          {start, end} = date;
    return {
      ...this.listService.prepareBaseQueryParams(value, params),
      due_today: true,
      statuses: orderStatuses ? orderStatuses.join(',') : null,
      assigned_to: orderAssignee ? orderAssignee.map((user: User) => user.id).join(',') : null,
      due_from: start ? this.timeService.formatDateTime(this.timeService.addStartDay(start)) : null,
      due_to: end ? this.timeService.formatDateTime(this.timeService.addEndDay(end)) : null
    };
  }

  public prepareCommissionsByMarketerListQueryParams(value: SearchBoxValue, params: IServerSideGetRowsParams): QueryParams {
    const {start, end} = value.date;
    return {
      ...this.listService.prepareBaseQueryParams(value, params),
      marketer_id: value.marketer?.id,
      from: start ? this.timeService.formatDateTime(this.timeService.addStartDay(start)) : null,
      to: end ? this.timeService.formatDateTime(this.timeService.addEndDay(end)) : null
    };
  }

  public prepareSentToClientByUserListQueryParams(value: SearchBoxValue, params: IServerSideGetRowsParams): QueryParams {
    const {start: begin, end} = value.date;
    return {
      ...this.listService.prepareBaseQueryParams(value, params),
      groupBy: 'completed_by',
      begin: begin ? this.timeService.formatDateTime(this.timeService.addStartDay(begin)) : null,
      end: end ? this.timeService.formatDateTime(this.timeService.addEndDay(end)) : null
    };
  }

  public prepareAccountingForManagerListQueryParams(value: SearchBoxValue, params: IServerSideGetRowsParams): QueryParams {
    const {date, orderStatuses} = value,
          {start, end} = date;
    return {
      ...this.listService.prepareBaseQueryParams(value, params),
      statuses: orderStatuses ? orderStatuses.join(',') : null,
      company_id: value.company?.id,
      from: start ? this.timeService.formatDateTime(this.timeService.addStartDay(start)) : null,
      to: end ? this.timeService.formatDateTime(this.timeService.addEndDay(end)) : null
    };
  }

  private getOrderQuantityByStatus(reports: Array<OrdersByStatus>, status: string): string {
    return <string>reports.find((report: OrdersByStatus) => report.status === status)?.cnt || '0';
  }
}
